/**
 * Device Data Service
 *
 * Handles device data: battery, usage, location history, water changes, filter life.
 * Single entry for /api/sw/device_data and related endpoints.
 */

import { apiClient } from './apiClient';
import buildParams from '../utils/params';

/**
 * Get battery history for a unit.
 */
export const getBatteryHistory = async (
  unitId,
  page = 1,
  pageSize = 10,
  extraParams = {},
) => {
  const params = {
    unitId,
    ...buildParams(extraParams, page, pageSize),
  };
  const response = await apiClient.get(`/api/sw/device_data/${unitId}/battery-history/`, {
    params,
  });
  return response.data;
};

/**
 * Get usage history for a unit.
 */
export const getUsageHistory = async (unitId, { esp32MacAddress, startDate, endDate }) => {
  const response = await apiClient.get(`/api/sw/device_data/${unitId}/usage-history/`, {
    params: {
      esp32_mac_address: esp32MacAddress,
      start_date: startDate,
      end_date: endDate,
    },
  });
  return response.data;
};

/**
 * Get location history.
 */
export const getLocationHistory = async (
  esp32_mac_address,
  page = 1,
  pageSize = 10,
  extraParams = {},
) => {
  const params = {
    esp32_mac_address,
    ...buildParams(extraParams, page, pageSize),
  };
  const response = await apiClient.get(`/api/sw/device_data/location-history/`, { params });
  return response.data;
};

/**
 * Get water changes history for a unit.
 */
export const getWaterChangesHistory = async (unitId, { esp32MacAddress, startDate, endDate }) => {
  const response = await apiClient.get(`/api/sw/device_data/${unitId}/water-changes/`, {
    params: {
      esp32_mac_address: esp32MacAddress,
      start_date: startDate,
      end_date: endDate,
    },
  });
  return response.data;
};

/**
 * Get filter life. Uses real API when available; returns empty until backend provides endpoint.
 */
export const getFilterLife = async (unitId, { esp32MacAddress, limit } = {}) => {
  try {
    const params = {};
    if (esp32MacAddress) params.esp32_mac_address = esp32MacAddress;
    if (limit != null) params.limit = limit;
    const response = await apiClient.get(`/api/sw/device_data/${unitId}/filter-life/`, {
      params,
    });
    return response?.data ?? { data: [] };
  } catch {
    return { data: [] };
  }
};

/**
 * Get locations visited last day with activity.
 */
export const getLocationsVisitedLastDay = async (deviceId) => {
  const response = await apiClient.get(
    `/api/sw/device_data/last-active-cleaned-locations/?device_id=${deviceId}`,
  );
  return response.data;
};
